import {useEffect} from 'react';
import PropTypes from 'prop-types';
import {propOr, mergeRight} from 'ramda';
import useForceUpdate from 'use-force-update';

import {getIconCategory, getFontSize} from '../utils';
import IconButton from '../internal_components/_IconButton.react';

/**
 * An app-level Hero component.
 *
 * **Example Usage**
 * ```
 * app.layout = ddk.App([
 *     ddk.Hero(
 *         title="GALAXIAL MODELS",
 *         description="Galaxial Models lets you embark on immersive celestial adventures, navigating the cosmos through captivating interactive simulations.",
 *         logo="https://dash.plotly.com/assets/images/plotly_logo_dark.png",
 *         tags=[
 *             ddk.Tag(text="**Data Updated:** Oct. 8, 2023", icon="circle-check"),
 *             ddk.Tag(text="**Created by:** Chris Parmer", icon="user"),
 *             ddk.Tag(text="**Data Source**: Plotly Sample Data (2023)", icon="database"),
 *         ],
 *         button={
 *             "text": "More Data",
 *             "icon": "arrow-up-from-bracket",
 *             "url": "https://www.plotly.com/",
 *         },
 *     )
 * ])
 * ```
 */
function Hero(props) {
    const {
        id,
        logo,
        title,
        description,
        tags,
        button,
        children,
        margin,
        padding,
        background_color,
        border_width,
        border_style,
        border_color,
        border_radius,
        box_shadow,
        title_font_color,
        title_font_family,
        title_font_size,
        subtitle_font_color,
        subtitle_font_family,
        subtitle_font_size,
    } = props;

    const forceUpdate = useForceUpdate();

    const handleThemeUpdate = () => {
        forceUpdate();
    };

    useEffect(() => {
        window.addEventListener('dash-theme-update', handleThemeUpdate);
        return () => {
            // unsubscribe event
            window.removeEventListener('dash-theme-update', handleThemeUpdate);
        };
    }, []);

    /* merge conditional styles from props */
    const style = mergeRight(
        {
            padding: padding,
            margin: margin,
            backgroundColor: background_color,
            borderWidth: border_width,
            borderStyle: border_style,
            borderColor: border_color,
            borderRadius: border_radius,
            boxShadow: box_shadow,
        },
        propOr({}, 'style', props)
    );

    const titleStyle = {
        color: title_font_color,
        fontFamily: title_font_family,
        fontSize: getFontSize(title_font_size),
    };

    const subtitleStyle = {
        color: subtitle_font_color,
        fontFamily: subtitle_font_family,
        fontSize: getFontSize(subtitle_font_size),
    };

    const category = getIconCategory(button?.icon?.category);

    return (
        <header
            className={`layout-hero-container ${propOr('', 'className', props)}`}
            id={id}
            style={style}
            data-contentalignment={window.dashTheme.header_content_alignment}
        >
            <div className={`hero-content`}>
                <div className={`hero-stack`}>
                    <div className="hero-info-cta">
                        <div className="hero-info ddk-eject">
                            {/* use the ddk-eject class to ignore ddk styling on header tags */}
                            <h1 className="hero-title" style={titleStyle}>
                                {title}
                            </h1>
                            <div
                                className="hero-description"
                                style={subtitleStyle}
                            >
                                {description}
                            </div>
                        </div>
                        {button && (
                            <div className="hero-button-container">
                                (
                                <IconButton
                                    label={button?.text}
                                    icon={
                                        button?.icon &&
                                        (typeof button?.icon === 'string' ? (
                                            <i
                                                className={`fa${category} fa-${button?.icon}`}
                                            ></i>
                                        ) : (
                                            <i
                                                className={`fa${category} fa-${button?.icon?.name}`}
                                            ></i>
                                        ))
                                    }
                                    onClick={() => {
                                        window.open(
                                            button.url ??
                                                'https://dash.plotly.com',
                                            '_blank'
                                        );
                                    }}
                                />
                                )
                            </div>
                        )}
                    </div>
                    {logo && (
                        <div className="hero-logo-container">
                            <img src={logo} alt={`Logo path: "${logo}"`} />
                        </div>
                    )}
                </div>
                {tags?.length > 0 && (
                    <div className={`hero-tags`}>
                        {tags?.map((tag, index) => {
                            return <div key={tag.key ?? index}>{tag}</div>;
                        })}
                    </div>
                )}
                {children}
            </div>
        </header>
    );
}

Hero.propTypes = {
    /**
     * The ID of this component, used to identify Dash components
     * in callbacks. The ID needs to be unique across all of the
     * components in an app.
     */
    id: PropTypes.string,

    /**
     * Optional user-defined CSS class for the Hero.
     */
    className: PropTypes.string,

    /**
     * Any optional children to pass to the Hero.
     */
    children: PropTypes.node,

    /**
     * Optional prop to specify the source (src) of an image that represents a logo within the component.
     * This prop should be assigned a string value containing the URL or file path to the logo image.
     */
    logo: PropTypes.string,

    /**
     * Prop to set the app title or heading displayed on the Hero.
     */
    title: PropTypes.string,

    /**
     * Prop to set the description or subtitle displayed on the Hero.
     */
    description: PropTypes.string,

    /**
     * Prop to display a number of tags in the hero. This should be a list of `ddk.Tag` components.
     */
    tags: PropTypes.node,

    /**
     * Prop to add a button to the Hero, where the button label is passed as a `text` string and an `icon` object
     * containing `name` (string) and `category` (string). Icons can be specified using FontAwesome icons by setting
     * the `name` to the desired FontAwesome icon class, and the optional category to `solid`, `regular`, or `brands`.
     */
    button: PropTypes.shape({
        text: PropTypes.string,
        /**
         * An optional icon to display on the tag, which can be either a string
         * representing a Font Awesome icon name (without the "fa-"
         * prefix) or an object specifying the icon name and category.
         */
        icon: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                /**
                 * The name of the Font Awesome icon without the "fa-" prefix.
                 */
                icon_name: PropTypes.string,

                /**
                 * The Font Awesome category for the icon, which must be one of the following:
                 * - "solid"
                 * - "regular"
                 * - "brands"
                 * - "light"
                 */
                icon_category: PropTypes.oneOf([
                    // PropTypes have to be inline in js, unhandled imports by react-docgen.
                    'solid',
                    'regular',
                    'brands',
                    'light',
                ]),
            }),
        ]),
        url: PropTypes.string,
    }),

    /**
     * Space (in pixels) surrounding the Hero.
     */
    margin: PropTypes.number,

    /**
     * Space (in pixels) on the inside of the Hero, between the border
     * and the edge of the content.
     */
    padding: PropTypes.number,

    /**
     * The background color applied to the Hero. Overrides theme.hero_background_color.
     */
    background_color: PropTypes.string,

    /**
     * The box shadow(s) applied to the Hero.
     */
    box_shadow: PropTypes.string,

    /**
     * The border width applied to the Hero.
     */
    border_width: PropTypes.string,

    /**
     * The border style applied to the Hero.
     */
    border_style: PropTypes.string,

    /**
     * The border color applied to the Hero.
     */
    border_color: PropTypes.string,

    /**
     * The border radius applied to the Hero.
     */
    border_radius: PropTypes.string,

    /**
     * The font color of the Hero title. Overrides theme.hero_title_text
     */
    title_font_color: PropTypes.string,

    /**
     * The font family of the Hero title. Overrides theme.font_family
     */
    title_font_family: PropTypes.string,

    /**
     * The font size of the Hero title.
     * Can be a relative size, "smaller", "normal", "larger",
     * or a CSS attribute size (e.g. `px`, 'em`)
     * Overrides theme.hero_title_font_size.
     */
    title_font_size: PropTypes.string,

    /**
     * The font color of the Hero description. Overrides theme.hero_subtitle_text
     */
    subtitle_font_color: PropTypes.string,

    /**
     * The font family of the Hero description. Overrides theme.font_family
     */
    subtitle_font_family: PropTypes.string,

    /**
     * The font size of the Hero description.
     * Can be a relative size, "smaller", "normal", "larger",
     * or a CSS attribute size (e.g. `px`, 'em`)
     * Overrides theme.hero_subtitle_font_size.
     */
    subtitle_font_size: PropTypes.string,

    /**
     * The style object of the outermost div of the Hero.
     * Use this to override e.g. the Hero's height
     * (`{'height': '100px'}`)
     */
    style: PropTypes.object,
};

export default Hero;
